import { lazy } from 'react';
import type { ComponentType, LazyExoticComponent } from 'react';

export const RELOAD_KEY = 'chunk-reload-at';
// Long enough to cover one reload and its retry; a second failure inside it means the chunk is really missing.
export const RELOAD_WINDOW_MS = 30000;

export function isChunkLoadError(error: unknown): boolean {
  const e = error as { name?: unknown; message?: unknown } | null;
  return (
    e?.name === 'ChunkLoadError' ||
    (typeof e?.message === 'string' &&
      /Loading (CSS )?chunk [\w-]+ failed/i.test(e.message))
  );
}

/** Whether to reload for a stale build: only once per window, so a chunk that is truly gone reaches the error boundary. */
export function shouldReloadForChunk(
  error: unknown,
  lastReloadAt: number | null,
  now: number
): boolean {
  return (
    isChunkLoadError(error) &&
    (lastReloadAt === null || now - lastReloadAt > RELOAD_WINDOW_MS)
  );
}

const readLastReload = (): number | null => {
  try {
    const raw = window.sessionStorage.getItem(RELOAD_KEY);
    return raw ? Number(raw) : null;
  } catch {
    return null;
  }
};

// After a deploy the open page still asks for the previous build's chunk names; one reload fetches the new build.
export function lazyWithReload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
): LazyExoticComponent<T> {
  return lazy(() =>
    factory().catch((error: unknown) => {
      if (
        typeof window !== 'undefined' &&
        shouldReloadForChunk(error, readLastReload(), Date.now())
      ) {
        try {
          window.sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
        } catch {
          // Without storage the window guard cannot hold, so let the boundary show the error instead.
          throw error;
        }
        window.location.reload();
        return new Promise<{ default: T }>(() => undefined);
      }
      throw error;
    })
  );
}
